import { Plus } from '@phosphor-icons/react';
import { Button, Celebration, Heading, Sheet, Text } from '@shh/ui';
import { useState } from 'react';
import { useLens } from '../app/lens';
import { shows } from '../app/view';
import { JobEditTip } from '../components/JobEdit';
import { JobForm } from '../components/taskForms';
import { ForEveryone, PersonTasks, useTick } from '../components/tasks';
import { kids, parents, type M } from '../data/derive';
import { useFamily } from '../data/family';
import { cardColumns } from '../lib/display';
import { tasksText } from '../i18n/tasks';

/**
 * All of today's tasks and to-dos – one card per person with the whole day inside (Morgens,
 * Nachmittags, Abends). With a lens, only that person (plus "für alle").
 */
export function TasksPage() {
  const f = useFamily();
  const lens = useLens();
  const [celebrate, setCelebrate] = useState<M | null>(null);
  const [adding, setAdding] = useState(false);
  const { tick, together, picker } = useTick(setCelebrate);
  const children = kids(f).filter((m) => shows(lens.id, m.id));
  const grown = parents(f).filter((m) => shows(lens.id, m.id));
  const canPlan = lens.canParent || f.role === 'teen';

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-gap-md">
        <Text tone="secondary" className="max-w-narrow">
          {tasksText.tapHint}
        </Text>
        <JobEditTip />
        {/* one button, one form "Einmal · Regelmäßig" (1.28) */}
        {canPlan && (
          <Button icon={Plus} onPress={() => setAdding(true)}>
            {tasksText.task}
          </Button>
        )}
      </div>

      {children.length > 0 && (
        <section aria-labelledby="kids" className="@container flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="kids">
            {tasksText.todayHeading}
          </Heading>
          {/* stacked columns: every card only as tall as its content, no empty gaps */}
          <div className="columns-cards gap-gap-md" style={cardColumns(children.length)}>
            {children.map((m) => (
              <div key={m.id} className="mb-gap-md break-inside-avoid">
                <PersonTasks
                  member={m}
                  daypart="all"
                  onTick={tick}
                  onTogether={together}
                  maxItems={30}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      <section aria-labelledby="todos" className="@container flex flex-col gap-gap-md">
        <Heading level={2} size="md" id="todos">
          {tasksText.todosAndAll}
        </Heading>
        {/* the parents' cards plus "für alle" and the shopping list */}
        <div className="columns-cards gap-gap-md" style={cardColumns(grown.length + 2)}>
          {grown.map((m) => (
            <div key={m.id} className="mb-gap-md break-inside-avoid">
              <PersonTasks
                member={m}
                daypart="all"
                onTick={tick}
                onTogether={together}
                maxItems={12}
              />
            </div>
          ))}
          <div className="mb-gap-md break-inside-avoid">
            <ForEveryone onTick={tick} onTogether={together} />
          </div>
        </div>
      </section>

      {picker}
      <Sheet
        isOpen={adding}
        onOpenChange={setAdding}
        title={tasksText.newTask}
        isDismissable={false}
      >
        <JobForm onDone={() => setAdding(false)} />
      </Sheet>
      <Celebration
        show={!!celebrate}
        title={celebrate ? tasksText.nameDone(celebrate.name) : ''}
        subtitle={tasksText.wellDone}
        onDone={() => setCelebrate(null)}
      />
    </>
  );
}
